import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DocumentSubType, Invoice } from 'src/app/core/interfaces/invoice.interface';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Filter } from 'src/app/core/interfaces/filter.interface';
import { DocumentPayload } from 'src/app/core/interfaces/document.interface';
import { BranchService } from 'src/app/core/services/branch.service';
import { RoundingSettingsService } from 'src/app/modules/settings/services/rounding-settings.service';
import { RoundingPipe } from 'src/app/shared/pipe/rounding.pipe';
import { Branch } from '../interfaces/branch.interface';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  private baseUrl = environment.apiBaseUrl;

  private defaultBranch: any;

  private branch: Branch | null = null;

  private roundingPipe: RoundingPipe;

  constructor(private http: HttpClient, private branchService: BranchService, private roundingSettingsService: RoundingSettingsService) {
    this.roundingPipe = new RoundingPipe(this.roundingSettingsService);
  }

  private getSeparatedEndpoint(type: DocumentSubType): string | null {
    switch (type) {
      case DocumentSubType.Sales:
        return 'Sales';
      case DocumentSubType.SalesReturn:
        return 'Sales/return';
      case DocumentSubType.Purches:
        return 'Purchase';
      case DocumentSubType.PurchesReturn:
        return 'Purchase/return';
      default:
        return null;
    }
  }

  private resolveDocumentEndpoint(type: DocumentSubType, suffix = ''): string {
    const separatedEndpoint = this.getSeparatedEndpoint(type);
    const endpoint = separatedEndpoint ? `/${separatedEndpoint}` : `/Document/${type}`;
    return `${this.baseUrl}${endpoint}${suffix}`;
  }

  getInvoice(id: number, type: DocumentSubType): Observable<Invoice> {
    return this.http.get<Invoice>(this.resolveDocumentEndpoint(type, `/${id}`));
  }

  getJournalByDocumentId(documentId: number | string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/Journal/Document/${documentId}`);
  }

  listInvoices(pageNumber = 1, pageSize = 10, term = '', type: DocumentSubType, orderBy?: string, filterBy?: Filter[]): Observable<any> {
    const data = {
      term,
      advancedFilter: filterBy ?? [],
      pageNumber,
      pageSize,
      orderBy: orderBy ? [orderBy] : undefined,
    };

    return this.http.post(this.resolveDocumentEndpoint(type, '/list'), data);
  }

  listZatcaInvoices(pageNumber = 1, pageSize = 10, term = '', orderBy?: string, filterBy?: Filter[]): Observable<any> {
    const data = {
      term,
      advancedFilter: filterBy ?? [],
      pageNumber,
      pageSize,
      orderBy: orderBy ? [orderBy] : undefined,
    };

    return this.http.post(`${this.baseUrl + `/Document/zatca-list`}`, data);
  }

  getInvoiceById(id: number, type: DocumentSubType, isCachable = false): Observable<Invoice> {
    const header = isCachable ? { headers: { 'X-Use-Cache': 'true' } } : {};

    return this.http.get<Invoice>(this.resolveDocumentEndpoint(type, `/${id}`), header);
  }

  createInvoice(data: any, type: DocumentSubType): Observable<any> {
    return this.http.post(this.resolveDocumentEndpoint(type), data);
  }

  updateInvoice(data: any, type: DocumentSubType): Observable<any> {
    return this.http.put(this.resolveDocumentEndpoint(type), data);
  }

  getPaymentMethodLookup(
    pageNumber = 1,
    pageSize = 50,
    term = '',
    advancedFilter: Filter[] = [],
  ): Observable<any> {
    return this.http.post(`${this.baseUrl}/Lookup/PaymentMethodLookup`, {
      term,
      advancedFilter,
      includeDeleted: false,
      pageNumber,
      pageSize,
      orderBy: [],
      category: 'ALL',
    });
  }

  deleteInvoice(id: number, type: DocumentSubType): Observable<any> {
    return this.http.delete(this.resolveDocumentEndpoint(type, `/${id}`));
  }

  createInvoices(data: any[], type: DocumentSubType, suppressErrorMessage: boolean = false): Observable<any> {
    const headers: { [key: string]: string } = {};
    if (suppressErrorMessage) {
      headers['X-Suppress-Error-Message'] = 'true';
    }
    
    return this.http.post(this.resolveDocumentEndpoint(type, '/bunch'),
      {
        documents: data
      },
      { headers }
    );
  }

  exportInvoices(type: DocumentSubType, filterBy?: Filter[]) {
    const data = {
      advancedFilter: filterBy ?? [],
      // "startDate": "2025-03-05",
      // "endDate": "2025-03-05",
      // "startId": 0,
      // "endId": 0,
      // "contactId": 0,
      // "documentSubTypeId": 0
    };

    return this.http.post(this.resolveDocumentEndpoint(type, '/export'), data, {
      responseType: 'blob'
    });
  }

  generateInvoice(id: string | number): Observable<any> {
    return this.http.post(`${this.baseUrl + `/Document/generate`}`, {
      "documentIds": [
        id
      ]
    });
  }

  generateAllInvoice(): Observable<any> {
    return this.http.post(`${this.baseUrl + `/Document/generate`}`, {
      "documentIds": [],
      "allDocuments": true
    });
  }

  generateSubsetInvoice(documentIds: string[]): Observable<any> {
    return this.http.post(`${this.baseUrl + `/Document/generate`}`, {
      "documentIds": documentIds,
      "allDocuments": false
    });
  }

  generateInvoiceBarcode(data: DocumentPayload, branch?: any) {
    // needs the branch default :) 
    return new Promise<string>((resolve, reject) => {
      // If branch is provided and valid, use it directly
      if (branch && (branch.VATRegistrationName || branch.vatRegistrationName)) {
        this.defaultBranch = branch;
        resolve(this._generateInvoiceBarcode(data));
        return;
      }

      // Otherwise, fetch default branch
      if (!this.defaultBranch)
        this.branchService.getDefaultBranch().subscribe({
          next: (branchResponse: any) => {
            this.defaultBranch = branchResponse.data;
            resolve(this._generateInvoiceBarcode(data));
          },
          error: (err) => {
            console.error('Error fetching default branch:', err);
            reject(err); // Re-throw the error to handle it in the calling code
          }
        });
      else resolve(this._generateInvoiceBarcode(data));
    });
  }

  _generateInvoiceBarcode(data: DocumentPayload) {
    // Calculate total without VAT (price * quantity for each line)
    const totalWithoutVat = data.documentLines.reduce((acc, line) => acc + ((line.price ?? 0) * line.quantity), 0);
    // Calculate VAT total - taxAmount is per unit, so multiply by quantity
    const vatTotal = data.documentLines.reduce((acc, line) => acc + ((line.taxAmount ?? 0) * line.quantity), 0);

    // Use correct property names from Branch interface (VATRegistrationName, VATRegistrationNumber)
    // Also support lowercase variants for backward compatibility
    const branch = this.defaultBranch;
    const sellerName = branch?.VATRegistrationName || branch?.vatRegistrationName || '';
    const vatRegistrationNumber = branch?.VATRegistrationNumber || branch?.vatRegistrationNumber || '';

    // Use invoice date from payload, fallback to current date
    const invoiceDate = data.documentDate ? new Date(data.documentDate).toISOString() : new Date().toISOString();

    const payload = {
      sellerName: sellerName,
      vatRegistrationNumber: vatRegistrationNumber,
      invoiceDate: invoiceDate,  // ISO 8601
      invoiceTotalWithVat: this.roundingPipe.transform(totalWithoutVat + vatTotal), // rounded using project settings
      vatTotal: this.roundingPipe.transform(vatTotal), // rounded using project settings
    };

    const sb = [];
    sb.push(this.toHex(1, payload.sellerName));
    sb.push(this.toHex(2, payload.vatRegistrationNumber));
    sb.push(this.toHex(3, payload.invoiceDate));
    sb.push(this.toHex(4, payload.invoiceTotalWithVat.toString()));
    sb.push(this.toHex(5, payload.vatTotal.toString()));

    const payloadHex = sb.join('');

    // Convert hex string to Uint8Array
    const byteArray = new Uint8Array(payloadHex.length / 2);
    for (let i = 0; i < payloadHex.length; i += 2) {
      byteArray[i / 2] = parseInt(payloadHex.substring(i, i + 2), 16);
    }

    // Base64 encode
    const payload64 = btoa(String.fromCharCode.apply(null, Array.from(byteArray)));

    return payload64;
  }

  toHex = (id: number, value: string) => {
    // Convert string to UTF8 bytes first
    const encoder = new TextEncoder();
    const bytes = encoder.encode(value);

    // Format: tag (1 byte) + length (1 byte) + value bytes
    const tag = id.toString(16).padStart(2, '0');
    const length = bytes.length.toString(16).padStart(2, '0');

    // Convert bytes to hex
    const valueHex = Array.from(bytes)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');

    return `${tag}${length}${valueHex}`;
  };
}
